import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Upload,
  App as AntApp,
} from "antd";
import {
  CheckCircleFilled,
  DownloadOutlined,
  InboxOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { RcFile } from "antd/es/upload";

import { catalogApi } from "../api/catalog";
import { usersApi } from "../api/users";
import type { BulkUploadResult } from "../api/users";
import type { Shop, User } from "../api/types";
import { Avatar } from "../components/Avatar";
import { PageHeader } from "../components/PageHeader";

export function Employees() {
  const { notification, modal } = AntApp.useApp();
  const [rows, setRows] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [createdCreds, setCreatedCreds] = useState<{
    employee_id: string | null;
    email: string;
    temp: string;
  } | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkUploadResult | null>(null);
  const [allocateUser, setAllocateUser] = useState<User | null>(null);
  const [allocateShopIds, setAllocateShopIds] = useState<string[]>([]);
  const [shopOptions, setShopOptions] = useState<Shop[]>([]);
  const [allocateLoading, setAllocateLoading] = useState(false);
  const [allocateSaving, setAllocateSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list({ role: "employee", search, page, page_size: pageSize });
      setRows(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const [addForm] = Form.useForm();
  const onAdd = async () => {
    const vals = await addForm.validateFields();
    try {
      const created = await usersApi.create({ ...vals, role: "employee" });
      setCreatedCreds({
        employee_id: created.employee_id,
        email: created.email,
        temp: (created as User & { temp_password: string }).temp_password,
      });
      setAddOpen(false);
      addForm.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, string[]> } };
      const data = err.response?.data;
      notification.error({ message: data ? JSON.stringify(data) : "Create failed" });
    }
  };

  const onResetPassword = async (u: User) => {
    modal.confirm({
      title: `Reset password for ${u.full_name}?`,
      okText: "Reset",
      onOk: async () => {
        const r = await usersApi.resetPassword(u.id);
        modal.info({
          title: "Temporary password",
          content: (
            <div>
              <div className="small-muted">Share securely with the employee:</div>
              <div className="mono" style={{ marginTop: 8, padding: 8, background: "var(--bg-subtle)", borderRadius: 6 }}>
                {r.temp_password}
              </div>
            </div>
          ),
        });
      },
    });
  };

  const onDeactivate = (u: User) => {
    modal.confirm({
      title: `Deactivate ${u.full_name}?`,
      content: "The employee will not be able to sign in until reactivated.",
      okButtonProps: { danger: true },
      onOk: async () => {
        await usersApi.update(u.id, { is_active: false });
        notification.success({ message: "Deactivated" });
        load();
      },
    });
  };

  const onActivate = async (u: User) => {
    await usersApi.update(u.id, { is_active: true });
    notification.success({ message: "Activated" });
    load();
  };

  const openAllocate = async (u: User) => {
    setAllocateUser(u);
    setAllocateLoading(true);
    setAllocateShopIds([]);
    try {
      const [allocated, allShopsRes] = await Promise.all([
        usersApi.listAllocatedShops(u.id),
        catalogApi.listShops({ page_size: 500 }),
      ]);
      setShopOptions(allShopsRes.results);
      setAllocateShopIds(allocated.map((s) => s.id));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({ message: err.response?.data?.detail || "Could not load shops" });
    } finally {
      setAllocateLoading(false);
    }
  };

  const saveAllocate = async () => {
    if (!allocateUser) return;
    setAllocateSaving(true);
    try {
      await usersApi.setAllocatedShops(allocateUser.id, allocateShopIds);
      notification.success({
        message: `${allocateShopIds.length} shop${allocateShopIds.length === 1 ? "" : "s"} allocated to ${allocateUser.full_name}`,
      });
      setAllocateUser(null);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({ message: err.response?.data?.detail || "Allocation failed" });
    } finally {
      setAllocateSaving(false);
    }
  };

  const columns = [
    {
      title: "",
      width: 48,
      render: (_: unknown, u: User) => <Avatar name={u.full_name} size={32} />,
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      render: (v: string | null) => <span className="mono" style={{ fontSize: 12 }}>{v || "—"}</span>,
    },
    { title: "Full Name", dataIndex: "full_name", render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span> },
    { title: "Phone", dataIndex: "phone", render: (v: string) => <span className="mono" style={{ fontSize: 12 }}>{v || "—"}</span> },
    {
      title: "Attendance",
      key: "attendance",
      width: 120,
      render: () => (
        <div style={{ width: 90 }}>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 3 }}>N/A</div>
          <div
            style={{
              height: 5,
              background: "var(--bg-hover)",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div style={{ width: "0%", height: "100%", background: "var(--success)", borderRadius: 999 }} />
          </div>
        </div>
      ),
    },
    {
      title: "Shops",
      dataIndex: "allocated_shop_ids",
      render: (v: string[] | undefined) => {
        const count = v?.length ?? 0;
        if (count === 0) return <Tag color="warning">None</Tag>;
        return <Tag color="blue">{count} allocated</Tag>;
      },
    },
    {
      title: "Incentive",
      key: "incentive",
      width: 100,
      render: () => <span className="small-muted">—</span>,
    },
    {
      title: "",
      render: (_: unknown, u: User) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "allocate",
                label: "Allocate Shops",
                icon: <ShopOutlined />,
                onClick: () => openAllocate(u),
              },
              { key: "reset", label: "Reset Password", onClick: () => onResetPassword(u) },
              u.is_active
                ? { key: "deactivate", label: "Deactivate", danger: true, onClick: () => onDeactivate(u) }
                : { key: "activate", label: "Activate", onClick: () => onActivate(u) },
            ],
          }}
          trigger={["click"]}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const beforeUpload = async (file: RcFile) => {
    try {
      const res = await usersApi.bulkUpload(file);
      setBulkResult(res);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({ message: err.response?.data?.detail || "Upload failed" });
    }
    return false;
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="Employees"
        subtitle="Manage employee accounts"
        right={
          <Space>
            <Button icon={<UploadOutlined />} onClick={() => setBulkOpen(true)}>
              Bulk Upload
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              Add Employee
            </Button>
          </Space>
        }
      />

      <div className="toolbar-row">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search name, email or phone…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 360 }}
        />
        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-secondary)" }}>
          {total} total
        </div>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      {/* Add single employee */}
      <Modal
        title="Add Employee"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={onAdd}
        okText="Create"
      >
        <Form layout="vertical" form={addForm} requiredMark={false}>
          <Form.Item label="Full Name" name="full_name" rules={[{ required: true, min: 2, max: 255 }]}>
            <Input placeholder="e.g. Ravi Kumar" />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true, type: "email" }]}>
            <Input placeholder="ravi@company.com" />
          </Form.Item>
          <Form.Item label="Phone (optional)" name="phone">
            <Input placeholder="10-digit mobile" />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="A temporary password will be generated and shown once. Share it with the employee securely."
          />
        </Form>
      </Modal>

      {/* Credentials after single create */}
      <Modal
        title="Employee Created"
        open={!!createdCreds}
        onCancel={() => setCreatedCreds(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setCreatedCreds(null)}>
            Done
          </Button>,
        ]}
      >
        {createdCreds && (
          <div>
            <div
              className="mono"
              style={{ padding: 12, background: "var(--bg-subtle)", borderRadius: 6, fontSize: 13 }}
            >
              <div>Employee ID: {createdCreds.employee_id || "—"}</div>
              <div>Email:       {createdCreds.email}</div>
              <div>Password:    {createdCreds.temp}</div>
            </div>
            <div className="small-muted" style={{ marginTop: 8 }}>
              Employees sign in with their Employee ID.
            </div>
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="This password will not be shown again. Copy it now."
            />
          </div>
        )}
      </Modal>

      {/* Bulk upload */}
      <Modal
        title="Bulk Upload Employees"
        open={bulkOpen}
        onCancel={() => {
          setBulkOpen(false);
          setBulkResult(null);
        }}
        width={640}
        footer={
          bulkResult && bulkResult.successful_rows > 0
            ? [
                <Button
                  key="credentials"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    window.open(usersApi.credentialsUrl(bulkResult.upload_id), "_blank");
                  }}
                >
                  Download Credentials CSV
                </Button>,
                <Button
                  key="done"
                  onClick={() => {
                    setBulkOpen(false);
                    setBulkResult(null);
                    load();
                  }}
                >
                  Done
                </Button>,
              ]
            : [<Button key="cancel" onClick={() => setBulkOpen(false)}>Cancel</Button>]
        }
      >
        {!bulkResult && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Step 1 — Download the template</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 10 }}>
              Fill in the template with up to 500 employees. Columns: email, full_name, phone.
            </div>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => window.open(usersApi.bulkTemplateUrl(), "_blank")}
            >
              Download Template.csv
            </Button>
            <div style={{ height: 1, background: "var(--border)", margin: "20px 0" }} />
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Step 2 — Upload your CSV</div>
            <Upload.Dragger name="file" accept=".csv" multiple={false} beforeUpload={beforeUpload} showUploadList={false}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Drop your CSV here, or click to browse</p>
              <p className="ant-upload-hint small-muted">Maximum 500 rows, 2 MB</p>
            </Upload.Dragger>
          </>
        )}

        {bulkResult && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <CheckCircleFilled style={{ fontSize: 48, color: "var(--success)" }} />
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 8 }}>
                {bulkResult.successful_rows} employees created
              </div>
              {bulkResult.failed_rows > 0 && (
                <Tag color="red" style={{ marginTop: 8 }}>
                  {bulkResult.failed_rows} rows had errors
                </Tag>
              )}
            </div>
            {bulkResult.errors.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Errors</div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => `${r.row_number}-${r.email}`}
                  dataSource={bulkResult.errors}
                  columns={[
                    { title: "Row", dataIndex: "row_number", width: 60 },
                    { title: "Email", dataIndex: "email" },
                    { title: "Reason", dataIndex: "reason" },
                  ]}
                />
              </>
            )}
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Temporary passwords were generated. Download the credentials file and distribute it securely."
            />
          </div>
        )}
      </Modal>

      {/* Allocate shops to employee */}
      <Modal
        title={allocateUser ? `Allocate Shops — ${allocateUser.full_name}` : "Allocate Shops"}
        open={!!allocateUser}
        onCancel={() => setAllocateUser(null)}
        onOk={saveAllocate}
        confirmLoading={allocateSaving}
        okText="Save Allocation"
        width={560}
      >
        <Alert
          type="info"
          showIcon
          message="The employee can only sell to shops listed here. Removing a shop revokes their access immediately."
          style={{ marginBottom: 16 }}
        />
        <Select
          mode="multiple"
          loading={allocateLoading}
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder={
            shopOptions.length === 0 ? "No shops available — add shops first" : "Select shops to allocate"
          }
          value={allocateShopIds}
          onChange={setAllocateShopIds}
          options={shopOptions.map((s) => ({
            label: `${s.name}${s.city ? ` — ${s.city}` : ""}`,
            value: s.id,
          }))}
          maxTagCount="responsive"
        />
      </Modal>
    </div>
  );
}
